import { useQuery } from '@tanstack/react-query';
import { Tags } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney, fmtPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

export function SetupBuckets() {
  const q = useQuery({ queryKey: ['setup-buckets'], queryFn: api.setupBuckets });

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Tags className="size-4 text-indigo-300" />
        <h3 className="text-sm font-medium text-zinc-200">Setup expectancy</h3>
        <span className="text-[11px] text-zinc-400 ml-auto">
          ranked by expectancy per trade
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-400">
            <tr>
              <Th>Setup</Th>
              <Th className="text-right">n</Th>
              <Th className="text-right">Win rate</Th>
              <Th className="text-right">Avg win</Th>
              <Th className="text-right">Avg loss</Th>
              <Th className="text-right">Payoff</Th>
              <Th className="text-right">Expectancy</Th>
              <Th className="text-right">Profit factor</Th>
              <Th className="text-right">Net</Th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.buckets ?? []).map((b) => (
              <tr key={b.tag} className="border-t border-zinc-900">
                <Td className="capitalize text-zinc-200">{b.tag}</Td>
                <Td className="text-right num text-zinc-300">{b.stats.closed}</Td>
                <Td className="text-right num">{fmtPercent(b.stats.winRate * 100)}</Td>
                <Td className="text-right num text-emerald-400">{fmtMoney(b.stats.avgWinner)}</Td>
                <Td className="text-right num text-rose-400">{fmtMoney(b.stats.avgLoser)}</Td>
                <Td className="text-right num">{b.stats.payoffRatio.toFixed(2)}×</Td>
                <Td
                  className={cn(
                    'text-right num',
                    b.stats.expectancy >= 0 ? 'text-emerald-400' : 'text-rose-400',
                  )}
                >
                  {fmtMoney(b.stats.expectancy)}
                </Td>
                <Td className="text-right num">{b.stats.profitFactor.toFixed(2)}</Td>
                <Td
                  className={cn(
                    'text-right num',
                    b.stats.netResult >= 0 ? 'text-emerald-300' : 'text-rose-300',
                  )}
                >
                  {fmtMoney(b.stats.netResult)}
                </Td>
              </tr>
            ))}
            {q.data?.buckets.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-zinc-400 py-6">
                  No tagged trades yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-zinc-400 mt-2 leading-snug">
        Expectancy = (win rate × avg win) + (loss rate × avg loss). Positive
        expectancy per setup means that bucket has an edge worth repeating.
      </p>
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn('font-normal px-2 py-1.5 whitespace-nowrap', className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-2 py-2 whitespace-nowrap', className)}>{children}</td>;
}
